import { useState } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, Eye, Lightbulb, Megaphone, MousePointerClick, Search } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { AdsPerformanceChart } from '@/components/ads/AdsPerformanceChart';
import { BudgetCard } from '@/components/ads/BudgetCard';
import { ClicksToSalesSummary } from '@/components/ads/ClicksToSalesSummary';
import { ShopMap } from '@/components/ads/ShopMap';
import { ColumnTable } from '@/components/ui/column-table';
import { EmptyState } from '@/components/ui/empty-state';
import { KpiTile } from '@/components/ui/kpi-tile';
import { SectionHeader } from '@/components/ui/section-header';
import { Skeleton } from '@/components/ui/skeleton';
import { SoftCard } from '@/components/ui/soft-card';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useLocalAds, useLocalAdsAverages } from '@/hooks/useInsights';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import myShopClient from '@services/nearlive';
import { LIA_INSIGHT_METRICS, SUMMARY_ROWS, resolveColumns } from '@/lib/liaInsights';
import { PageScore } from '@/components/overview/LocalPotentialHeader';

const numberFormatter = new Intl.NumberFormat('en-GB');

function formatNumber(n) {
	return numberFormatter.format(Number(n) || 0);
}

function AdsLoadingState() {
	return (
		<div className="space-y-10">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Ads</h1>
					<p className="mt-2 max-w-2xl text-muted-foreground">Loading your Local Inventory Ads performance…</p>
				</div>
			</header>
			<SoftCard className="h-44 animate-pulse" />
			<section className="grid grid-cols-2 gap-4 md:grid-cols-4">
				{[0, 1, 2, 3].map((i) => (
					<SoftCard key={i} className="min-h-[170px] !p-5">
						<Skeleton className="mx-auto h-3.5 w-2/3" />
						<Skeleton className="mx-auto mt-4 h-8 w-1/2" />
						<Skeleton className="mx-auto mt-4 h-5 w-1/3" />
					</SoftCard>
				))}
			</section>
			<SoftCard className="h-72 animate-pulse" />
		</div>
	);
}

/**
 * Shown while Google hasn't approved the store for Local Inventory Ads
 * serving (`serving.state` from /api/local-ads). Activation is blocked
 * server-side in this state — this card is the retailer-facing explanation.
 */
function LiaReviewNotice({ enabled = false, issues = false, accountIssue = false }) {
	return (
		<SoftCard className="flex flex-wrap items-start gap-4 bg-warning-soft/60">
			<AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-warning-soft-foreground" />
			<div className="min-w-0 flex-1">
				<h3 className="text-base font-bold text-foreground">
					{accountIssue
						? 'Google has restricted this Merchant Center account'
						: issues
							? 'Google has flagged issues with your products'
							: 'Google is reviewing your store'}
				</h3>
				<p className="mt-1 text-sm text-muted-foreground">
					{accountIssue
						? enabled
							? "Google has restricted this store's Merchant Center account under its business policies, so your ads can't show — this isn't caused by your product data. Contact support@unify.near.st and we'll help you resolve it with Google."
							: "Local Ads can't be switched on while Google has this store's Merchant Center account restricted under its business policies — it isn't caused by your product data. Contact support@unify.near.st and we'll help you resolve it with Google."
						: issues
							? enabled
								? "Your ads can't show right now because Google has disapproved your products for Local Inventory Ads. Fixing your product data will get them showing again — or contact support@unify.near.st for help."
								: "Local Ads can't be switched on while Google has your products disapproved for Local Inventory Ads. Fix the product issues — or contact support@unify.near.st for help — and you'll be able to activate."
							: enabled
								? "Your ads won't show to shoppers until Google finishes reviewing your store and products for Local Inventory Ads — this usually takes a few days. Your budget is only spent when ads actually show."
								: "Google reviews every new store before Local Inventory Ads can show — this usually takes a few days. You'll be able to activate ads as soon as the review completes."}
				</p>
			</div>
		</SoftCard>
	);
}

/**
 * Newly-activated shops haven't accumulated any Google Ads insights yet
 * (Google takes a few hours to start serving Local Inventory Ads after a
 * plan goes live). Rather than rendering the full populated dashboard with
 * zeroes everywhere — which reads as "broken" — show a warming-up state
 * that keeps the BudgetCard available (so the user can still tune their
 * commitment) and previews the metrics they'll see once data lands.
 */
function AdsWarmingUpState({ budget, onSave, liaBlocked = false, liaIssues = false, liaAccountIssue = false }) {
	return (
		<div className="space-y-10">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Ads</h1>
					<p className="mt-2 max-w-2xl text-muted-foreground">
						{liaAccountIssue
							? "Your budget is ready — but Google has restricted this store's Merchant Center account. Your ads will start showing once it's resolved."
							: liaIssues
								? "Your budget is ready — but Google has flagged issues with your products. Your ads will start showing once they're resolved."
								: liaBlocked
									? 'Your budget is ready — Google is still reviewing your store. Your ads will start showing automatically once the review completes.'
									: 'Your ads are live. Google takes a few hours to start showing them — your real numbers will land here as soon as the first impressions come in.'}
					</p>
				</div>
			</header>

			{liaBlocked ? <LiaReviewNotice enabled issues={liaIssues} accountIssue={liaAccountIssue} /> : null}

			<BudgetCard initialDaily={budget.daily ?? 5} onSave={onSave} />

			<EmptyState
				icon={Megaphone}
				eyebrow={
					liaAccountIssue || liaIssues ? 'Needs attention' : liaBlocked ? 'Waiting for Google' : 'Coming in the next 24 hours'
				}
				title={
					liaAccountIssue
						? 'Google has restricted this account'
						: liaIssues
							? 'Google has flagged your products'
							: liaBlocked
								? 'Google is reviewing your products'
								: 'Your first results are warming up'
				}
				body={
					liaAccountIssue
						? "Local Inventory Ads can't show while Google has this store's Merchant Center account restricted. Contact support@unify.near.st and we'll help you resolve it with Google — your first results will appear here once ads start serving."
						: liaIssues
							? "Local Inventory Ads can't show while Google has your products disapproved. Fix the product issues — or contact support@unify.near.st for help — and your first impressions, clicks and search terms will appear here once ads start serving."
							: liaBlocked
								? "Local Inventory Ads can't show until Google finishes reviewing your store and products — for new stores this usually takes a few days. Your first impressions, clicks and search terms will appear here once ads start serving."
								: 'It usually takes Google a few hours to start serving Local Inventory Ads. Your first impressions, clicks and search terms will appear here as soon as they start coming through.'
				}
				previewTitle="What you'll see here"
				preview={[
					{ icon: Eye, label: 'Local shoppers reached', detail: 'Impressions across Google and Maps' },
					{ icon: MousePointerClick, label: 'Clicks to your store', detail: 'Calls, directions, product views' },
					{ icon: Search, label: 'Search terms locals are using', detail: 'See exactly how they find you' }
				]}
			/>
		</div>
	);
}

function AdsEmptyState({
	onActivate,
	startOnBudget = false,
	hasCard = false,
	onAddCard,
	addingCard = false,
	cardError = null,
	liaBlocked = false,
	liaIssues = false,
	liaAccountIssue = false
}) {
	const { shopPath } = useShopPath();
	// Don't open the budget step without a card on file — there's nothing to
	// activate yet, so the no-card path routes to "add a card" first. While
	// Google's LIA review is pending the budget step stays closed too: the
	// backend rejects the activation, so offering the slider would dead-end.
	const [showingBudget, setShowingBudget] = useState(startOnBudget && hasCard && !liaBlocked);

	if (showingBudget) {
		return (
			<div className="space-y-8">
				<button
					type="button"
					onClick={() => setShowingBudget(false)}
					className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
				>
					<ArrowLeft className="h-3 w-3" /> Back
				</button>
				<header>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Activate Local Ads</h1>
					<p className="mt-2 max-w-2xl text-muted-foreground">
						Choose your daily budget. You can stop or change it any time — we&rsquo;ll never spend more than this per day.
					</p>
				</header>
				<BudgetCard mode="activation" initialDaily={5} onSave={onActivate} />
			</div>
		);
	}

	// Local Ads bills daily as the retailer spends (NEA-5753), so a card has to
	// be on file before activation. With no card the primary CTA collects one
	// via Stripe Checkout instead of opening the budget step; the backend
	// (REQUIRE_PAYMENT_METHOD_FOR_LIA) enforces the same rule as a backstop.
	const noCard = !hasCard;

	return (
		<div className="space-y-10">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Ads</h1>
					<p className="mt-2 text-muted-foreground">Put your in-store stock in front of locals searching on Google right now.</p>
				</div>
				<div className="ml-auto shrink-0">
					<PageScore />
				</div>
			</header>
			{liaBlocked ? <LiaReviewNotice issues={liaIssues} accountIssue={liaAccountIssue} /> : null}
			<EmptyState
				icon={Megaphone}
				eyebrow="Local Ads — your secret weapon"
				title="Reach shoppers searching nearby today"
				body={
					liaBlocked
						? liaAccountIssue
							? "Local Ads automatically promote what's in stock to people Googling nearby. Google has restricted this store's Merchant Center account, so ads can't be switched on yet — contact support@unify.near.st and we'll help you resolve it with Google."
							: liaIssues
								? "Local Ads automatically promote what's in stock to people Googling nearby. Google has disapproved your products for Local Inventory Ads, so ads can't be switched on yet — fixing your product data unlocks activation."
								: "Local Ads automatically promote what's in stock to people Googling nearby. Google is still reviewing your store, so ads can't be switched on just yet — you'll be able to activate them the moment the review completes."
						: noCard
							? "Local Ads bills daily as you spend, so you'll need a card on file before you can switch it on. Add one and you're ready to go."
							: "Local Ads automatically promote what's in stock to people Googling nearby. Most retailers see over £5 of in-store sales for every £1 spent."
				}
				primaryCta={liaBlocked ? undefined : noCard ? (addingCard ? 'Redirecting…' : 'Add a payment method') : 'Start with £5/day'}
				onPrimaryClick={liaBlocked ? undefined : noCard ? onAddCard : onActivate ? () => setShowingBudget(true) : undefined}
				primaryHref={shopPath('/channels/local-ads')}
				secondaryCta="See how it works"
				secondaryHref={shopPath('/channels/local-ads')}
				previewTitle="What you'll start tracking"
				preview={[
					{ icon: Eye, label: 'Impressions in your local area', detail: 'How many people saw your products' },
					{ icon: MousePointerClick, label: 'Clicks straight to your store', detail: 'Calls, directions and product views' },
					{ icon: Search, label: 'Search terms locals are using', detail: 'Spot trends before competitors do' }
				]}
			/>
			{noCard && cardError ? <p className="mt-4 text-sm font-medium text-destructive">{cardError}</p> : null}
		</div>
	);
}

export function Ads() {
	const { shop, isLoading: shopLoading } = useShop();
	const { data, isLoading: adsLoading, updateBudget, activate, updateAverageBasketValue } = useLocalAds(shop?.id);
	const { billing, isLoading: billingLoading } = useBillingStatus(shop?.id);
	const { data: averages } = useLocalAdsAverages();
	const { shopPath } = useShopPath();
	const [searchParams] = useSearchParams();
	const [addingCard, setAddingCard] = useState(false);
	const [cardError, setCardError] = useState(null);
	const loading = shopLoading || adsLoading || billingLoading;

	// A card on file is required before Local Ads can be switched on (it bills
	// daily as you spend). `hasPaymentMethod` comes from the same billing read
	// AppLayout already loaded, so this is a warm SWR cache hit, not a new call.
	const hasCard = Boolean(billing?.hasPaymentMethod);

	// No card → send the retailer to Stripe Checkout (mode: setup) to add one,
	// mirroring the Billing page's "Add a card" CTA. It returns to the Billing
	// page; they come back to Ads and activate (now card-on-file). Card-checkout
	// 403s under HQ impersonation by design — surface that rather than swallow.
	const addCard = async () => {
		if (!shop?.id) return;
		setAddingCard(true);
		setCardError(null);
		try {
			const client = await myShopClient();
			const { data: session } = await client.post(`/api/billing/card-checkout/${shop.id}`);
			if (!session?.url) throw new Error('Stripe Checkout returned no redirect URL');
			window.location.assign(session.url);
		} catch (err) {
			setCardError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Could not start card setup');
			setAddingCard(false);
		}
	};

	// `/api/local-ads/average-results` aggregates `shop.weeklyInsights` across
	// shops with a live plan. In production it produces a real benchmark; for
	// new tenants and the dev environment it returns 0/0. We fall back to the
	// industry-published Local Inventory Ads benchmark (~1.8% CTR), which is
	// what the Lovable design uses verbatim. Keeps the caption meaningful
	// rather than empty.
	const INDUSTRY_FALLBACK_CTR = '1.80';
	const benchmarkCtr =
		averages && Number(averages.impressions) > 0
			? ((Number(averages.clicks) / Number(averages.impressions)) * 100).toFixed(2)
			: INDUSTRY_FALLBACK_CTR;
	const ctrIndustryCaption = `Industry average: ${benchmarkCtr}%`;

	// Whether Google will actually serve Local Inventory Ads for this store —
	// derived server-side from the merchant's LFP serving verification and the
	// LOCAL_INVENTORY_ADS product statuses (NEA-6127). Absent (older API) means
	// no verdict, which renders as the previous always-live behaviour.
	const serving = data?.serving;
	const liaBlocked = Boolean(serving && serving.state !== 'live');
	const liaIssues = serving?.state === 'issues';
	const liaAccountIssue = Boolean(serving?.reasons?.includes('account-policy-enforcement'));

	if (loading) return <AdsLoadingState />;
	if (!data?.enabled)
		return (
			<AdsEmptyState
				onActivate={shop?.id ? activate : undefined}
				startOnBudget={searchParams.get('activate') === '1'}
				hasCard={hasCard}
				onAddCard={addCard}
				addingCard={addingCard}
				cardError={cardError}
				liaBlocked={liaBlocked}
				liaIssues={liaIssues}
				liaAccountIssue={liaAccountIssue}
			/>
		);

	const summary = data.summary || {};
	const budget = data.budget || { daily: 0, monthly: 0 };
	const series = Array.isArray(data.daily) ? data.daily : [];
	const topProducts = Array.isArray(data.topProducts) ? data.topProducts : [];
	const searchTerms = Array.isArray(data.searchTerms) ? data.searchTerms : [];
	const locations = Array.isArray(data.locations) ? data.locations : [];
	const clicksToSales = Array.isArray(data.clicksToSales) ? data.clicksToSales : [];
	const clicksToSalesSummary = data.clicksToSalesSummary || { sales: 0, revenue: 0, basketRevenue: 0 };

	// Just-activated shops have an enabled plan but zero Google-Ads-side data
	// yet. Render a warming-up surface instead of a populated dashboard full
	// of zeros — keep the BudgetCard so they can tune their commitment.
	if (!summary.impressions && !summary.clicks)
		return (
			<AdsWarmingUpState
				budget={budget}
				onSave={updateBudget}
				liaBlocked={liaBlocked}
				liaIssues={liaIssues}
				liaAccountIssue={liaAccountIssue}
			/>
		);

	const cpcDisplay = summary.cpc ? `£${Number(summary.cpc).toFixed(2)}` : '£0.00';
	const ctrDisplay = `${Number(summary.ctr || 0).toFixed(2)}%`;
	const monthlySpendDisplay = `£${formatNumber(summary.monthlySpend || budget.monthly || 0)}`;

	return (
		<div className="space-y-10">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Ads</h1>
					<p className="mt-2 max-w-2xl text-muted-foreground">
						Activate or change your ads budget to appear at the top of Google results and promote your products to more local
						shoppers.
					</p>
				</div>
				<div className="ml-auto shrink-0">
					<PageScore />
				</div>
			</header>

			{liaBlocked ? <LiaReviewNotice enabled issues={liaIssues} accountIssue={liaAccountIssue} /> : null}

			{data.pausedForBilling ? (
				<SoftCard className="flex flex-wrap items-start gap-4 border border-destructive/30 bg-destructive/5">
					<AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-destructive" />
					<div className="min-w-0 flex-1">
						<h3 className="text-base font-bold text-destructive">Your ads are paused because a payment failed</h3>
						<p className="mt-1 text-sm text-muted-foreground">
							We couldn't charge your card for recent ad spend, so we've paused your Google Ads campaigns. Update your payment
							method to resume them — we'll restart automatically once the outstanding invoice clears.
						</p>
					</div>
					<Link
						to="/settings/billing"
						className="inline-flex items-center gap-1.5 rounded-full bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground transition hover:opacity-90"
					>
						Update payment <ArrowRight className="h-4 w-4" />
					</Link>
				</SoftCard>
			) : null}

			<BudgetCard initialDaily={budget.daily ?? 5} onSave={updateBudget} />

			<section>
				<SectionHeader
					eyebrow="Performance"
					title="How your ads are performing"
					sub="Headline numbers and daily trend, compared to the previous period."
				/>
				<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
					<KpiTile label="Local shoppers reached" value={formatNumber(summary.impressions)} delta={summary.impressionsChange} />
					<KpiTile label="Clicks to your store" value={formatNumber(summary.clicks)} delta={summary.clicksChange} />
					<KpiTile label="Click-through rate" value={ctrDisplay} sub={ctrIndustryCaption} />
					<KpiTile label="Cost per click" value={cpcDisplay} sub={`${monthlySpendDisplay} ad spend this month`} />
				</div>
				<SoftCard className="mt-4 p-5 md:p-6">
					<AdsPerformanceChart series={series} />
				</SoftCard>
			</section>

			<section className="grid grid-cols-1 gap-5 xl:grid-cols-2">
				<div>
					<SectionHeader title={LIA_INSIGHT_METRICS.products.title} sub={LIA_INSIGHT_METRICS.products.sub} />
					<ColumnTable
						rows={topProducts.slice(0, SUMMARY_ROWS)}
						emptyLabel={LIA_INSIGHT_METRICS.products.emptyLabel}
						viewMoreHref={shopPath('/ads/insights/products')}
						columns={LIA_INSIGHT_METRICS.products.columns}
					/>
				</div>
				<div>
					<SectionHeader title={LIA_INSIGHT_METRICS['search-terms'].title} sub={LIA_INSIGHT_METRICS['search-terms'].sub} />
					<ColumnTable
						rows={searchTerms.slice(0, SUMMARY_ROWS)}
						emptyLabel={LIA_INSIGHT_METRICS['search-terms'].emptyLabel}
						viewMoreHref={shopPath('/ads/insights/search-terms')}
						columns={LIA_INSIGHT_METRICS['search-terms'].columns}
					/>
				</div>
			</section>

			<section>
				<SectionHeader
					eyebrow="Advanced insights"
					title="Where your ads are appearing"
					sub="The local areas where shoppers are seeing and clicking your ads."
				/>
				<div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
					<SoftCard className="overflow-hidden p-0 xl:col-span-2">
						<div className="grid grid-cols-1 md:grid-cols-2">
							<div className="h-full border-b border-border/60 md:border-b-0 md:border-r">
								<ShopMap shop={shop} label={shop?.city || locations[0]?.location} radiusKm={data.radiusKm} />
							</div>
							<div>
								<div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-6 border-b border-border/60 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
									<div>Location</div>
									<div className="w-16 text-right">Clicks</div>
									<div className="w-20 text-right">Views</div>
								</div>
								<div className="divide-y divide-border/60">
									{locations.length === 0 ? (
										<div className="px-5 py-6 text-center text-sm text-muted-foreground">No geographic data yet.</div>
									) : (
										locations.map((l) => (
											<div
												key={l.location}
												className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-6 px-5 py-3"
											>
												<div className="truncate text-sm font-medium">{l.location}</div>
												<div className="w-16 text-right text-sm font-semibold tabular-nums text-foreground">
													{formatNumber(l.clicks)}
												</div>
												<div className="w-20 text-right text-sm tabular-nums text-muted-foreground">
													{formatNumber(l.views)}
												</div>
											</div>
										))
									)}
								</div>
							</div>
						</div>
					</SoftCard>

					<SoftCard tone="muted">
						<div className="flex items-center gap-2 text-sm font-bold text-foreground">
							<Lightbulb className="h-4 w-4 text-primary" /> How can I use these insights?
						</div>
						<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
							Spotting which areas drive the most clicks helps you decide where to focus stock and promotions. If a nearby
							town is sending lots of views but few clicks, your product titles or photos may need a refresh.
						</p>
						<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
							Want to expand your radius?{' '}
							<button className="font-semibold text-primary hover:underline">Contact support</button>.
						</p>
					</SoftCard>
				</div>
			</section>

			<section>
				<SectionHeader title={LIA_INSIGHT_METRICS['clicks-to-sales'].title} sub={LIA_INSIGHT_METRICS['clicks-to-sales'].sub} />
				<ClicksToSalesSummary
					summary={clicksToSalesSummary}
					currency={shop?.currency}
					onSaveBasketValue={updateAverageBasketValue}
				/>
				<div className="mt-4">
					<ColumnTable
						rows={clicksToSales.slice(0, SUMMARY_ROWS)}
						emptyLabel={LIA_INSIGHT_METRICS['clicks-to-sales'].emptyLabel}
						viewMoreHref={shopPath('/ads/insights/clicks-to-sales')}
						columns={resolveColumns(LIA_INSIGHT_METRICS['clicks-to-sales'], { currency: shop?.currency })}
					/>
				</div>
			</section>

			<SoftCard tone="soft" className="flex flex-wrap items-center gap-4">
				<div className="min-w-0 flex-1">
					<h3 className="text-lg font-bold">Your ads are paying back</h3>
					<p className="mt-1 max-w-xl text-sm text-muted-foreground">
						For every £1 you spend, you're being seen by more local shoppers. Bumping your budget will help you grow further.
					</p>
				</div>
				<a
					href="#top"
					className="inline-flex items-center gap-1.5 rounded-full bg-action px-5 py-2.5 text-sm font-semibold text-action-foreground transition hover:opacity-90"
					onClick={(e) => {
						e.preventDefault();
						window.scrollTo({ top: 0, behavior: 'smooth' });
					}}
				>
					Adjust budget <ArrowRight className="h-4 w-4" />
				</a>
			</SoftCard>
		</div>
	);
}
